import { AxiosResponse } from 'axios';
import { SET_SHIUREI_TORAHS, SET_SHIUREI_TORAH, SET_ERRORMSG } from '@/store/mutation_types';
import {
  BULK_DELETE_SHIUREI_TORAHS,
  BULK_MOVE_SHIUREI_TORAHS_TO_FOLDER,
  CLEAR_SHIUREI_TORAH_ISLOADING,
  DELETE_SHIUREI_TORAH,
  EDIT_SHIUREI_TORAH,
  EXPORT_SHIUREI_TORAHS,
  GET_SHIUREI_TORAHS,
  MOVE_SHIUREI_TORAH_TO_FOLDER,
  REGISTER_SHIUREI_TORAH,
  TOGGLE_SHIUREI_TORAH_IS_ACTIVE,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import ShiureiTorah from '@/services/shiurei_torah';
import { Module } from 'vuex';
import { EditShiureiTorah, MoveShiureiTorahToFolder, MoveShiureiTorahsToFolder, RegisterShiureiTorah, GetShiureiTorah } from '@/dtos/shiurei_torah.dto';

const buildPaginationMeta = (metaData: any, payload: any) => {
  const perPage = Number(metaData?.perPage ?? payload?.limit ?? 20);
  const currentPage = Number(metaData?.currentPage ?? payload?.page ?? 1);
  const totalRecords = Number(metaData?.totalRecords ?? 0);
  const totalPages = Number(metaData?.totalPages ?? Math.max(Math.ceil(totalRecords / perPage), 1));

  return { currentPage, perPage, totalRecords, totalPages };
};

const shiureiTorah: Module<any, {}> = {
  state: {
    shiureiTorah: {
      data: null,
      isLoading: false,
    },
    shiureiTorahs: {
      data: null,
      metaData: null,
      isLoading: false,
    },
  },
  getters: {
    shiureiTorah: (state: { shiureiTorah: any }) => state.shiureiTorah,
    shiureiTorahs: (state: { shiureiTorahs: any }) => state.shiureiTorahs,
  },
  mutations: {
    [SET_SHIUREI_TORAH]: (
      state: { shiureiTorah: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.shiureiTorah.data = payload.data) : null,
        (state.shiureiTorah.isLoading = payload.isLoading);
    },
    [SET_SHIUREI_TORAHS]: (
      state: { shiureiTorahs: StateObject },
      payload: { data: null; metaData?: any; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.shiureiTorahs.data = payload.data) : null,
        payload.metaData != null ? (state.shiureiTorahs.metaData = payload.metaData) : null,
        (state.shiureiTorahs.isLoading = payload.isLoading);
    },
  },
  actions: {
    [REGISTER_SHIUREI_TORAH]: async ({ commit }: any, payload: any) => {
      commit(SET_SHIUREI_TORAH, { data: null, isLoading: true });

      const result: AxiosResponse = await ShiureiTorah.registerShiureiTorah(
        payload as RegisterShiureiTorah
      );

      if (result) {
        if (result.data.status) {
          commit(SET_SHIUREI_TORAH, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SHIUREI_TORAH, { isLoading: false });
        return false;
      }
    },
    [GET_SHIUREI_TORAHS]: async ({ commit }: any, payload: any) => {
      commit(SET_SHIUREI_TORAHS, { data: null, isLoading: true });

      const result: AxiosResponse = await ShiureiTorah.getShiureiTorahs(
        payload as GetShiureiTorah
      );

      if (result) {
        if (result.data.status) {
          const records = Array.isArray(result.data.data) ? result.data.data : [];
          const metaData = buildPaginationMeta(result.data.metaData, payload);
          commit(SET_SHIUREI_TORAHS, { data: records, metaData, isLoading: false });
          return true;
        }
        commit(SET_SHIUREI_TORAHS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SHIUREI_TORAHS, { isLoading: false });
        return false;
      }
    },
    // Fetches shiureiTorahs for export WITHOUT mutating the paginated
    // `shiureiTorahs` list that backs the visible table, so the table behind the
    // export modal stays intact. Returns the raw records array (or null on failure).
    [EXPORT_SHIUREI_TORAHS]: async (_ctx: any, payload: any) => {
      const result: AxiosResponse = await ShiureiTorah.getShiureiTorahs(
        payload as GetShiureiTorah
      );

      if (result && result.data && result.data.status) {
        return Array.isArray(result.data.data) ? result.data.data : [];
      }

      return null;
    },
    [MOVE_SHIUREI_TORAH_TO_FOLDER]: async ({ commit }: any, payload: any) => {
      commit(SET_SHIUREI_TORAH, { data: { id: payload.id }, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await ShiureiTorah.moveShiureiTorahToFolder(
          payload as MoveShiureiTorahToFolder
        );
      } catch (error) {
        commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
        throw error;
      }

      if (result) {
        if (result.data.status) {
          commit(SET_SHIUREI_TORAH, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SHIUREI_TORAH, { isLoading: false });
        return false;
      }
    },
    [BULK_MOVE_SHIUREI_TORAHS_TO_FOLDER]: async ({ commit }: any, payload: MoveShiureiTorahsToFolder) => {
      commit(SET_SHIUREI_TORAH, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await ShiureiTorah.moveShiureiTorahsToFolder(payload);
      } catch (error) {
        commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
        throw error;
      }

      if (result?.data?.status) {
        commit(SET_SHIUREI_TORAH, { data: result.data.data, isLoading: false });
        return true;
      }

      commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
      if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [TOGGLE_SHIUREI_TORAH_IS_ACTIVE]: async ({ commit, state }: any, payload: any) => {
      commit(SET_SHIUREI_TORAH, { data: { id: payload.id }, isLoading: true });

      const result: AxiosResponse = await ShiureiTorah.toggleShiureiTorahIsActive(
        payload as EditShiureiTorah
      );

      if (result) {
        if (result.data.status) {
          let index;
          if (state.shiureiTorahs.data) {
            state.shiureiTorahs.data.forEach((e: any, i: number) => {
              if (e.id == result.data.data.id) {
                index = i;
              }
            });
            if (index != null)
              state.shiureiTorahs.data[index].isActive = result.data.data.isActive;
          }
          commit(SET_SHIUREI_TORAH, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SHIUREI_TORAH, { isLoading: false });
        return false;
      }
    },
    [EDIT_SHIUREI_TORAH]: async ({ commit }: any, payload: any) => {
      commit(SET_SHIUREI_TORAH, { data: null, isLoading: true });

      const result: AxiosResponse = await ShiureiTorah.editShiureiTorah(
        payload as EditShiureiTorah
      );

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_SHIUREI_TORAH, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SHIUREI_TORAH, { isLoading: false });
        return false;
      }
    },
    [DELETE_SHIUREI_TORAH]: async ({ commit }: any, payload: string) => {
      commit(SET_SHIUREI_TORAH, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await ShiureiTorah.deleteShiureiTorah(payload);
      } catch (error) {
        commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
        throw error;
      }

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_SHIUREI_TORAH, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SHIUREI_TORAH, { isLoading: false });
        return false;
      }
    },
    [BULK_DELETE_SHIUREI_TORAHS]: async ({ commit }: any, ids: string[]) => {
      commit(SET_SHIUREI_TORAH, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await ShiureiTorah.deleteShiureiTorahs(ids);
      } catch (error) {
        commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
        throw error;
      }

      if (result?.data?.status) {
        commit(SET_SHIUREI_TORAH, { data: result.data.data, isLoading: false });
        return true;
      }

      commit(SET_SHIUREI_TORAH, { data: null, isLoading: false });
      if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [CLEAR_SHIUREI_TORAH_ISLOADING]: ({ commit }) => {
      commit(SET_SHIUREI_TORAH, { isLoading: false });
      commit(SET_SHIUREI_TORAHS, { isLoading: false });
    },
  },
};

export default shiureiTorah;
